import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../api.js";
import styles from "../admin.module.css";

interface EventSummary {
  id: number;
  year: number;
  fame: number;
  textDe: string;
  status: "draft" | "published";
  categoryIds: number[];
}

interface Category {
  id: number;
  slug: string;
  nameDe: string;
}

export function EventsList() {
  const [events, setEvents] = useState<EventSummary[] | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");

  useEffect(() => {
    adminApi.get<Category[]>("/api/admin/categories").then(setCategories);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    if (categoryFilter) params.set("categoryId", categoryFilter);
    const t = setTimeout(() => {
      adminApi.get<EventSummary[]>(`/api/admin/events?${params.toString()}`).then(setEvents);
    }, 200);
    return () => clearTimeout(t);
  }, [search, statusFilter, categoryFilter]);

  async function handleDuplicate(id: number) {
    await adminApi.post(`/api/admin/events/${id}/duplicate`);
    adminApi.get<EventSummary[]>("/api/admin/events").then(setEvents);
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Karte wirklich löschen?")) return;
    await adminApi.delete(`/api/admin/events/${id}`);
    setEvents((prev) => prev?.filter((e) => e.id !== id) ?? null);
  }

  const categoryName = (id: number) => categories.find((c) => c.id === id)?.nameDe ?? "?";

  return (
    <div>
      <h1 className={styles.pageTitle}>LineUp-Karten</h1>

      <div className={styles.actions}>
        <Link to="/admin/karten/neu" className={styles.button}>
          Neue Karte
        </Link>
      </div>

      <div className={styles.card}>
        <div className={styles.formRow}>
          <label htmlFor="ev-search">Suche</label>
          <input
            id="ev-search"
            className={styles.input}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Text durchsuchen …"
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="ev-status">Status</label>
          <select
            id="ev-status"
            className={styles.select}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Alle</option>
            <option value="draft">Entwurf</option>
            <option value="published">Veröffentlicht</option>
          </select>
        </div>
        <div className={styles.formRow}>
          <label htmlFor="ev-category">Kategorie</label>
          <select
            id="ev-category"
            className={styles.select}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">Alle</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nameDe}
              </option>
            ))}
          </select>
        </div>
      </div>

      {events && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Jahr</th>
              <th>Bekanntheit</th>
              <th>Text (DE)</th>
              <th>Status</th>
              <th>Kategorien</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td>{e.year}</td>
                <td>{e.fame}</td>
                <td>{e.textDe}</td>
                <td>
                  <span className={e.status === "published" ? styles.statusPublished : styles.statusDraft}>
                    {e.status === "published" ? "Veröffentlicht" : "Entwurf"}
                  </span>
                </td>
                <td>{e.categoryIds.map(categoryName).join(", ")}</td>
                <td>
                  <Link to={`/admin/karten/${e.id}`} className={styles.buttonSecondary}>
                    Bearbeiten
                  </Link>{" "}
                  <button type="button" className={styles.buttonSecondary} onClick={() => handleDuplicate(e.id)}>
                    Duplizieren
                  </button>{" "}
                  <button type="button" className={styles.buttonDanger} onClick={() => handleDelete(e.id)}>
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
